/**
 * The live timing demos, offered where the live race sits when none is on: the dashboard, beside
 * the next round. Two races played again on the live race page:
 *   - WRL's Sunday 7 Hour at Sebring, replayed by the server as the timing came in live (server
 *     live/replay.ts, the listing's demo);
 *   - IMSA WeatherTech and the Michelin Pilot Challenge at Road Atlanta, replayed from their
 *     published races (useRaceReplay.ts).
 * Hidden while a session is live (the live banner has it).
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { fetchLiveListing } from "../../hooks/useLiveChartData";
import { seriesText, shortEvent, type LiveListing } from "./banner";
import { RACE_REPLAY_PREFIX } from "./useRaceReplay";

/** The 2026 IMSA races at Road Atlanta (RaceTrace's race ids): the IMSA demos. */
export const IMSA_DEMOS = [
  { raceId: "cmv9kouqq3701qh0papihai67", label: "IMSA WeatherTech · Road Atlanta" },
  { raceId: "cmuyi8z7517mbpa0p6wz8wqsh", label: "IMSA Pilot Challenge · Road Atlanta" },
];

/** The demos open on the Track Map. */
const DEMO_TAB = "?tab=map";

export interface DemoLink {
  to: string;
  label: string;
}

/** The demos to offer: the server's WRL replay when it has one, and the IMSA races. None while a session is live. */
export function demoLinks(list: LiveListing | null): DemoLink[] {
  if (!list || list.sessions.length > 0) return [];
  const out: DemoLink[] = [];
  const d = list.demo;
  if (d) out.push({ to: `/live/${d.slug}${DEMO_TAB}`, label: seriesText(d.series, `${shortEvent(d.eventName, "")} · ${d.sessionName}`) });
  for (const d of IMSA_DEMOS) out.push({ to: `/live/${RACE_REPLAY_PREFIX}${d.raceId}${DEMO_TAB}`, label: d.label });
  return out;
}

export function LiveDemoCard() {
  const [list, setList] = useState<LiveListing | null>(null);
  useEffect(() => {
    let alive = true;
    fetchLiveListing()
      .then((l) => alive && setList(l))
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, []);
  const links = demoLinks(list);
  if (!links.length) return null;
  return (
    <section aria-label="Live timing demos" className="flex flex-col gap-2 rounded-xl border border-[var(--rt-border)] bg-[var(--rt-surface)] px-4 py-3">
      <span className="text-xs font-semibold uppercase tracking-[0.06em] text-[var(--rt-accent)]">Live timing demos</span>
      <ul className="m-0 flex list-none flex-col gap-1 p-0">
        {links.map((l) => (
          <li key={l.to}>
            <Link to={l.to} className="flex min-h-[44px] items-center gap-3 rounded-lg hover:brightness-110 sm:min-h-[36px]">
              <span aria-hidden className="inline-block h-0 w-0 shrink-0 border-y-[7px] border-l-[11px] border-y-transparent border-l-[var(--rt-accent)]" />
              <span className="text-[15px] font-semibold">{l.label}</span>
            </Link>
          </li>
        ))}
      </ul>
      <span className="text-xs text-[var(--rt-text-muted)]">Replayed as they ran: the track map, running order, pit stops and race control.</span>
    </section>
  );
}
